/**
 * Formatting
 *
 * Degrees to rāśi-degree-minute-second, Devanāgarī numerals, locale-aware dates,
 * and duration in years-months-days for daśā periods.
 *
 * ⚠ **Rounding a longitude for display must never change which division it is
 * in.** 29°59′59.6″ of Meṣa displayed as "30°00′00″ Meṣa" is a contradiction, and
 * "0°00′00″ Vṛṣabha" is a different rāśi from the one the chart computed. So the
 * rāśi is taken from the *unrounded* longitude and only the degrees inside it are
 * rounded — and if rounding would carry past 30°, it is clamped instead.
 *
 * © Manish Jagdish Thatte
 */

import { DEVANAGARI_LOCALES } from '../config.js';
import { degreesInRashi, rashiAt } from '../jyotish/constants.js';

const DEVANAGARI_DIGITS = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];

/** Latin digits in a string → Devanāgarī. Leaves everything else alone. */
export function toDevanagariNumerals(text) {
  return String(text).replace(/[0-9]/g, (d) => DEVANAGARI_DIGITS[Number(d)]);
}

/**
 * Apply the locale's numeral system to an already-formatted string.
 *
 * `numerals` is 'locale' (follow the locale), 'latin', or 'devanagari'.
 * ◆ The setting wins over the locale, because a Marathi speaker who wants Latin
 * digits in a printed chart is making a reasonable request.
 */
export function applyNumerals(text, locale = 'en', numerals = 'locale') {
  const useDevanagari = numerals === 'devanagari'
    || (numerals === 'locale' && DEVANAGARI_LOCALES.includes(locale));
  return useDevanagari ? toDevanagariNumerals(text) : String(text);
}

/**
 * Split a decimal degree into d/m/s, rounding to whole seconds.
 *
 * Returns `{ d, m, s, carried }`. `carried` is true when rounding the seconds
 * rolled over into the next degree, which the caller may need to know about.
 */
export function toDMS(deg) {
  if (!Number.isFinite(deg)) throw new TypeError('toDMS: deg must be finite');
  const sign = deg < 0 ? -1 : 1;
  const abs = Math.abs(deg);
  let d = Math.floor(abs);
  let rem = (abs - d) * 60;
  let m = Math.floor(rem);
  let s = Math.round((rem - m) * 60);
  let carried = false;
  if (s === 60) { s = 0; m += 1; }
  if (m === 60) { m = 0; d += 1; carried = true; }
  return { sign, d, m, s, carried };
}

const pad2 = (n) => String(n).padStart(2, '0');

/** `12°34′56″`. */
export function formatDMS(deg, { seconds = true } = {}) {
  const { sign, d, m, s } = toDMS(deg);
  const body = seconds ? `${d}°${pad2(m)}′${pad2(s)}″` : `${d}°${pad2(m)}′`;
  return (sign < 0 ? '−' : '') + body;
}

/**
 * A sidereal longitude as `Siṁha 12°34′56″`.
 *
 * ⚠ The rāśi comes from the unrounded longitude. See the header note: a graha at
 * 149°59′59.7″ is in Siṁha, and must not be shown as Kanyā 0°00′00″.
 */
export function formatRashiDMS(siderealLon, { locale = 'en', seconds = true, abbr = false } = {}) {
  const r = rashiAt(siderealLon);
  const within = degreesInRashi(siderealLon);
  const { d, m, s, carried } = toDMS(within);
  // Clamp rather than carry: 30°00′00″ of a rāśi is not a position in it.
  const dd = carried && d >= 30 ? 29 : d;
  const mm = carried && d >= 30 ? 59 : m;
  const ss = carried && d >= 30 ? 59 : s;
  const body = seconds ? `${dd}°${pad2(mm)}′${pad2(ss)}″` : `${dd}°${pad2(mm)}′`;
  const name = abbr ? r.abbr : r.name[locale] ?? r.name.en;
  return `${name} ${body}`;
}

/** The compact `11 Sc 37` form used in many Indian ephemerides. */
export function formatCompactLon(siderealLon) {
  const r = rashiAt(siderealLon);
  const { d, m } = toDMS(degreesInRashi(siderealLon));
  return `${d} ${r.abbr} ${pad2(m)}`;
}

/**
 * A UTC offset in minutes as `+05:30` or `+05:21:10`.
 *
 * ⚠ Seconds are shown when the offset has them, because they are the whole point
 * for a pre-1906 Indian birth: +05:21:10 is Madras Mean Time and +05:30 is not,
 * and 32 minutes of difference is about 8° of lagna. Truncating to
 * hours-and-minutes would hide exactly the fact this project keeps insisting on.
 */
export function formatOffset(minutes) {
  if (!Number.isFinite(minutes)) throw new TypeError('formatOffset: minutes must be finite');
  const sign = minutes < 0 ? '−' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = Math.floor(abs % 60);
  const s = Math.round((abs - Math.floor(abs)) * 60);
  return s
    ? `${sign}${pad2(h)}:${pad2(m)}:${pad2(s)}`
    : `${sign}${pad2(h)}:${pad2(m)}`;
}

/**
 * A duration in days as the classical `Y-M-D` form.
 *
 * ⚠ **Not decimal years.** A daśā of "7.34 years" is not how any classical text
 * or any pañcāṅga states a period, and it makes two implementations impossible
 * to compare. `yearDays` is explicit because there is a live controversy over
 * whether a daśā "year" is solar (365.2425 d) or sāvana (360 d) — this site uses sāvana
 * where the classical tables do, and says so is not a recommendation. Phase 7 owns the
 * choice; this function refuses to bury it in a constant.
 */
export function formatYMD(days, { yearDays = 365.2425, monthDays = null } = {}) {
  if (!Number.isFinite(days)) throw new TypeError('formatYMD: days must be finite');
  const mDays = monthDays ?? yearDays / 12;
  const y = Math.floor(days / yearDays);
  let rest = days - y * yearDays;
  const m = Math.floor(rest / mDays);
  rest -= m * mDays;
  const d = Math.floor(rest);
  return `${y}y ${m}m ${d}d`;
}

/** Latitude as `21°38′32″ N`. */
export function formatLatitude(lat) {
  return `${formatDMS(Math.abs(lat))} ${lat < 0 ? 'S' : 'N'}`;
}

/**
 * A Julian Day (UT) as a local wall-clock string at a fixed UTC offset.
 *
 * `offsetMinutes` is the zone's offset at that instant — never assumed to be
 * +05:30. Seconds are shown when `seconds` is true (default for pañcāṅga end
 * times, where a minute matters).
 */
export function formatJdLocal(jdUT, offsetMinutes, {
  seconds = true,
  dateStyle = true
} = {}) {
  if (!Number.isFinite(jdUT)) return '—';
  if (!Number.isFinite(offsetMinutes)) throw new TypeError('formatJdLocal: offsetMinutes must be finite');
  // Shift into "fake UTC" that equals local wall time, then read UTC fields.
  const ms = (jdUT - 2440587.5) * 86400000 + offsetMinutes * 60000;
  const d = new Date(ms);
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  const time = seconds
    ? `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
    : `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  if (!dateStyle) return time;
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${time}`;
}

/**
 * Longitude as `69°36′33″ E`.
 *
 * ⚠ `lonEast` is east-positive, everywhere in this codebase. A negative value is
 * West and is labelled so; it is never silently flipped.
 */
export function formatLongitude(lonEast) {
  return `${formatDMS(Math.abs(lonEast))} ${lonEast < 0 ? 'W' : 'E'}`;
}

/** A percentage or fraction as a short readable string. */
export function formatFraction(f, digits = 1) {
  return `${(f * 100).toFixed(digits)}%`;
}

/** A retrograde / combust marker set, for a planet table row. */
export function grahaFlags(g) {
  const out = [];
  if (g.retrograde) out.push('R');
  if (g.combust) out.push('C');
  return out;
}
